import React, { useMemo } from 'react';

import { Box } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types/swiper-options';

import { BREAKPOINTS_VALUES, useIsWidthUp } from '../../theme';
import { User } from '../../types';
import { splitIntoChunks } from '../../utils/helpers';
import { ExpertCard } from '../ExpertCard';
import { ExpertCardType } from '../ExpertCard/ExpertCard';
import useStyles from './styles';

const swiperSettings: SwiperOptions = {
  slidesPerView: 1,
  spaceBetween: 16,
  pagination: {
    clickable: true,
  },
  mousewheel: {
    releaseOnEdges: true,
    forceToAxis: true,
    invert: true,
  },
  navigation: true,
  observer: true,
  breakpoints: {
    [BREAKPOINTS_VALUES.ml]: {
      slidesPerView: 7,
    },
    [BREAKPOINTS_VALUES.xl]: {
      slidesPerView: 6,
    },
    1000: {
      slidesPerView: 5,
    },
    [BREAKPOINTS_VALUES.md]: {
      slidesPerView: 4,
    },
  },
};

const CHUNK_MOBILE_LENGTH = 4;
const CHUNK_TABLET_LENGTH = 6;

interface ExpertsSliderProps {
  experts?: User[];
  withLinks?: boolean;
  tileType?: ExpertCardType;
}

export const ExpertsSlider: React.FC<ExpertsSliderProps> = ({
  experts,
  withLinks = false,
  tileType = 'light',
}) => {
  const classes = useStyles();
  const isMobile = !useIsWidthUp('sm');
  const isTablet = !useIsWidthUp('md');

  const chunkLength = useMemo(() => {
    return isMobile ? CHUNK_MOBILE_LENGTH : CHUNK_TABLET_LENGTH;
  }, [isMobile]);

  const chunks = useMemo(() => {
    return isTablet ? splitIntoChunks(experts, chunkLength) : experts;
  }, [isTablet, chunkLength, experts]);

  return (
    <div className={classes.swiperWrapper}>
      <Swiper {...swiperSettings}>
        {chunks &&
          chunks.map((chunk) =>
            isTablet ? (
              <SwiperSlide key={chunk?.[0]?.id}>
                <Box className={classes.chunkWrapper}>
                  {chunk &&
                    chunk.map((expert) => (
                      <div key={`c-${expert?.id}`} className={classes.chunk}>
                        <ExpertCard
                          title={expert?.name}
                          imageUrl={expert?.avatar}
                          jobTitle={expert?.job_title}
                          slug={expert?.slug}
                          withLink={withLinks}
                          type={tileType}
                        />
                      </div>
                    ))}
                </Box>
              </SwiperSlide>
            ) : (
              <SwiperSlide key={chunk?.id}>
                <Box>
                  <ExpertCard
                    title={chunk?.name}
                    imageUrl={chunk?.avatar}
                    jobTitle={chunk?.job_title}
                    slug={chunk?.slug}
                    withLink={withLinks}
                    type={tileType}
                  />
                </Box>
              </SwiperSlide>
            ),
          )}
      </Swiper>
    </div>
  );
};

export default ExpertsSlider;
